/**
 * The chat thread's scroll arithmetic, shared by the admin chat screen and the client's
 * (Gabriel 2026-10-06: shared so the two never drift). useChatThreadScroll applies it to the DOM;
 * everything here takes plain numbers or the few element fields it reads, so it is testable
 * without layout.
 *
 * Bottom origin (chat-thread-bottom-anchored-scroll D1, D2): the thread scroller is a
 * `column-reverse` flex container, so `scrollTop` is 0 at the newest entry and negative upward,
 * and content added above the view never moves what is on screen. This file is the only place
 * that interprets a `scrollTop`; the screens read distances and directions from it.
 */

/** Distance from the bottom edge that still counts as pinned to the newest entry. */
export const PIN_SLACK_PX = 80;
/** The least distance from the top at which older history is requested. */
export const OLDER_TRIGGER_PX = 120;
/** Older history is requested this many screens before the top, so a swipe rarely reaches it first. */
export const OLDER_TRIGGER_SCREENS = 4;
/** Silence after the last scroll (or the finger lifting) before the thread counts as at rest (D8). */
export const REST_MS = 120;

/** How far above the newest entry the view's bottom edge sits. A rubber band past the bottom reads below zero. */
export function fromBottom(scrollTop: number): number {
  return 0 - scrollTop;
}

/** True within the slack of the newest entry, a rubber band past it included. */
export function isPinned(scrollTop: number): boolean {
  return fromBottom(scrollTop) <= PIN_SLACK_PX;
}

/** How far the view's top edge sits below the oldest loaded entry. */
export function distanceToTop(scrollTop: number, scrollHeight: number, clientHeight: number): number {
  return scrollHeight - clientHeight + scrollTop;
}

/** True when the reader is close enough to the oldest loaded entry that the next older page should be requested. */
export function shouldLoadOlder(toTop: number, clientHeight: number): boolean {
  return toTop < Math.max(OLDER_TRIGGER_PX, clientHeight * OLDER_TRIGGER_SCREENS);
}

/** Growth smaller than this is never a page of history (a font, a clamp, a failure line). */
export const STALE_MIN_GROWTH_PX = 200;
/** A move this far past a page's growth is still the reader's own step (a wheel click, a trackpad swipe). */
export const STALE_STEP_PX = 600;
/** The first scroll this soon after a commit can carry a step worked out before it. */
export const STALE_MS = 300;

/**
 * The race behind a leap (seen in Chrome on the website's panel, 2026-10-28): the browser runs wheel
 * and fling scrolling off the page's own thread, so older history can join above the view in the
 * same frame as a scroll step worked out before it. That step is then applied from the oldest end
 * instead of the newest, and the view leaps one page's height toward the oldest entries. Given how
 * much joined above the entry the reader holds and how far that entry drifted down the view since
 * (up is positive), the correction that puts the reader back with their own step kept; 0 when the
 * move is the reader's alone.
 */
export function staleStepCorrection(grew: number, movedUp: number): number {
  if (grew < STALE_MIN_GROWTH_PX) return 0;
  const own = movedUp - grew;
  return own >= -2 && own <= STALE_STEP_PX ? grew : 0;
}

/** Up is toward older history (a more negative `scrollTop`), down toward the newest entry. */
export type ScrollDirection = 'up' | 'down' | 'none';

/** The direction of one step between two scroll offsets; a step of `minStep` px or less is none. */
export function scrollDirection(prev: number, now: number, minStep = 0): ScrollDirection {
  if (now < prev - minStep) return 'up';
  if (now > prev + minStep) return 'down';
  return 'none';
}

/**
 * Pinned to the newest entry after a scroll event: pinned again within the slack, unpinned
 * only when the reader scrolled up. Content growing under a pinned thread is not a scroll and
 * must not unpin it.
 */
export function nextPinned(pinned: boolean, scrollTop: number, lastTop: number): boolean {
  if (isPinned(scrollTop)) return true;
  if (scrollDirection(lastTop, scrollTop) === 'up') return false;
  return pinned;
}

/** An entry's vertical extent on screen. */
export interface EntryBox {
  id: string;
  top: number;
  bottom: number;
}

/** The place the reader holds: the entry at the top of the view and how far its top sits below the view's top. */
export interface Anchor {
  id: string;
  offset: number;
}

/**
 * The first entry still showing at the top of the view (its bottom more than `slack` px below
 * the view's top), and its offset; null when none is.
 */
export function firstVisible(viewTop: number, boxes: Iterable<EntryBox>, slack = 0): Anchor | null {
  for (const box of boxes) {
    if (box.bottom > viewTop + slack) return { id: box.id, offset: box.top - viewTop };
  }
  return null;
}

/** How far to scroll so the anchored entry sits at its remembered offset again; 0 under a pixel. */
export function anchorCorrection(anchorTop: number, viewTop: number, offset: number): number {
  const delta = anchorTop - viewTop - offset;
  return Math.abs(delta) >= 1 ? delta : 0;
}

/**
 * The place to hold after a commit (D4): the anchored entry while it is still there, otherwise
 * its nearest older neighbour that survived, at the offset it had. `boxes` are the entries
 * measured with the anchor, oldest first, their `top` relative to the view's top; `entries` are
 * the ones on screen now. Null when neither the anchor nor an older neighbour survived.
 */
export function nearestSurvivingAnchor(
  anchorId: string,
  boxes: readonly EntryBox[],
  entries: readonly { id: string }[],
): Anchor | null {
  const present = new Set(entries.map((entry) => entry.id));
  const at = boxes.findIndex((box) => box.id === anchorId);
  if (at < 0) return null;
  for (let index = at; index >= 0; index -= 1) {
    const box = boxes[index];
    if (present.has(box.id)) return { id: box.id, offset: box.top };
  }
  return null;
}

/** Whether the reader is moving the thread (D8): a finger on the glass, or a scroll not yet followed by silence. */
export interface RestState {
  touching: boolean;
  /** The last scroll event or finger lift; null once the engine said the scroll ended. */
  lastActiveAt: number | null;
}

export const AT_REST: RestState = { touching: false, lastActiveAt: null };

export type ScrollGesture = 'scroll' | 'touchstart' | 'touchend' | 'touchcancel' | 'scrollend';

/** The rest state after one gesture event at `at` ms. */
export function restState(state: RestState, gesture: ScrollGesture, at: number): RestState {
  switch (gesture) {
    case 'scroll':
      return { ...state, lastActiveAt: at };
    case 'touchstart':
      return { ...state, touching: true };
    case 'touchend':
    case 'touchcancel':
      // A lifted finger may start a glide on the next frame: rest still waits for the silence.
      return { touching: false, lastActiveAt: at };
    case 'scrollend':
      return { ...state, lastActiveAt: null };
    default:
      return state;
  }
}

/** At rest: no finger down, and the engine ended the scroll or `REST_MS` passed in silence. */
export function isAtRest(state: RestState, now: number): boolean {
  return !state.touching && (state.lastActiveAt === null || now - state.lastActiveAt >= REST_MS);
}

/** Milliseconds until the thread is at rest if nothing else happens; null while a finger is down. */
export function restDueIn(state: RestState, now: number): number | null {
  if (state.touching) return null;
  if (state.lastActiveAt === null) return 0;
  return Math.max(0, REST_MS - (now - state.lastActiveAt));
}
